import type { Product } from '@features/products/types/products.types';
import type { SmriqGeneratedService } from '../types/smriq.types';
import { minimumConfiguredServices } from './minimumConfiguredServices';
import { resolveEffectiveServiceCountBounds } from './resolveServiceCountMode';
import { SmriqServiceMappingError } from './mapSmriqServices';
import { AUTO_MATCH_TOO_FEW_VISITS_MESSAGE } from './smriqGenerateError';

export interface SmriqServiceCountBounds {
    min: number;
    max: number;
}

// quote-service's `quote.validation.max_services`, still enforced when the product's trio is unreadable.
const QUOTE_MAX_SERVICES = 10;

export function resolveSmriqServiceCountBounds(
    product: Product | null | undefined,
): SmriqServiceCountBounds {
    const effective = resolveEffectiveServiceCountBounds(product?.config_settings);
    return {
        min: Math.max(1, minimumConfiguredServices(product), effective?.min ?? 0),
        max: effective?.max ?? QUOTE_MAX_SERVICES,
    };
}

export const UNCONFIGURED_SMRIQ_SERVICE_COUNT_BOUNDS = resolveSmriqServiceCountBounds(null);

const tooFewMessage = (count: number, min: number): string =>
    `This plan has ${count} scheduled visit${count === 1 ? '' : 's'}, fewer than the ${min} this product requires. Choose a longer duration.`;

export const NO_VISITS_ALLOWED_MESSAGE =
    'This product allows no scheduled visits, so a service plan cannot be added to it.';

/** Rejects a plan below `min` or bounds that admit no visit; keeps the first `max` visits, in schedule order. */
export function boundSmriqServices(
    services: SmriqGeneratedService[],
    autoMatch: boolean,
    bounds: SmriqServiceCountBounds = UNCONFIGURED_SMRIQ_SERVICE_COUNT_BOUNDS,
): SmriqGeneratedService[] {
    if (bounds.max < bounds.min) {
        throw new SmriqServiceMappingError(NO_VISITS_ALLOWED_MESSAGE);
    }
    if (services.length < bounds.min) {
        throw new SmriqServiceMappingError(
            autoMatch ? AUTO_MATCH_TOO_FEW_VISITS_MESSAGE : tooFewMessage(services.length, bounds.min),
        );
    }
    return services.slice(0, bounds.max);
}
